import dbAdapter from "./dbAdapter.js";
import realtimeClient from "./realtimeClient.js";
import Ajv from "ajv";
import addFormats from "ajv-formats";

// Create Ajv instance with options
// strict: false allows unknown keywords like "name", "description", etc. (common in JSON Schema for documentation)
const ajv = new Ajv({
    allErrors: true,
    useDefaults: true,
    strict: false,
});
// Add format validators (date, date-time, email, etc.)
addFormats(ajv);

/**
 * BaseEntity - Base class for all database entities
 * Provides CRUD operations and validation against schema
 */
class BaseEntity {
    static collectionName = null;
    static _schema = {};
    static _validateCache = new Map(); // Cache compiled validators

    // Normalize property schema (recursively handle nested objects and arrays)
    static normalizePropertySchema(propertySchema) {
        if (!propertySchema || typeof propertySchema !== "object") {
            return propertySchema;
        }

        const normalized = { ...propertySchema };

        // Convert exclusiveMinimum/exclusiveMaximum to numbers if they exist
        if (normalized.exclusiveMinimum !== undefined) {
            normalized.exclusiveMinimum = Number(normalized.exclusiveMinimum);
            if (isNaN(normalized.exclusiveMinimum)) {
                // If conversion fails, remove it
                delete normalized.exclusiveMinimum;
            }
        }

        if (normalized.exclusiveMaximum !== undefined) {
            normalized.exclusiveMaximum = Number(normalized.exclusiveMaximum);
            if (isNaN(normalized.exclusiveMaximum)) {
                // If conversion fails, remove it
                delete normalized.exclusiveMaximum;
            }
        }

        // Convert minimum/maximum to numbers if they exist
        if (normalized.minimum !== undefined) {
            normalized.minimum = Number(normalized.minimum);
            if (isNaN(normalized.minimum)) {
                delete normalized.minimum;
            }
        }

        if (normalized.maximum !== undefined) {
            normalized.maximum = Number(normalized.maximum);
            if (isNaN(normalized.maximum)) {
                delete normalized.maximum;
            }
        }

        // Recursively normalize nested properties (for objects)
        if (normalized.properties) {
            const normalizedProperties = {};
            Object.entries(normalized.properties).forEach(([key, value]) => {
                normalizedProperties[key] = this.normalizePropertySchema(value);
            });
            normalized.properties = normalizedProperties;
        }

        // Recursively normalize items (for arrays)
        if (normalized.items) {
            if (Array.isArray(normalized.items)) {
                normalized.items = normalized.items.map((item) =>
                    this.normalizePropertySchema(item)
                );
            } else {
                normalized.items = this.normalizePropertySchema(
                    normalized.items
                );
            }
        }

        return normalized;
    }

    // Normalize schema to JSON Schema format (support both formats)
    static normalizeSchema(schema) {
        // If already in JSON Schema format (has properties), ensure type is set and normalize properties
        if (schema.properties !== undefined) {
            const normalized = {
                type: "object",
                ...schema,
            };

            // Normalize all properties
            if (normalized.properties) {
                const normalizedProperties = {};
                Object.entries(normalized.properties).forEach(
                    ([key, value]) => {
                        normalizedProperties[key] =
                            this.normalizePropertySchema(value);
                    }
                );
                normalized.properties = normalizedProperties;
            }

            return normalized;
        }

        // Convert legacy format to JSON Schema format
        const properties = {};
        const required = [];

        Object.entries(schema).forEach(([key, config]) => {
            properties[key] = this.normalizePropertySchema({ ...config });
            if (config.required) {
                required.push(key);
                // Remove required from property config (it's not part of JSON Schema property definition)
                delete properties[key].required;
            }
        });

        return {
            type: "object",
            properties,
            required,
        };
    }

    // Get or create compiled validator for this schema
    static getValidator() {
        const schemaKey = JSON.stringify(this._schema);

        if (!this._validateCache.has(schemaKey)) {
            const normalizedSchema = this.normalizeSchema(this._schema);
            const validate = ajv.compile(normalizedSchema);
            this._validateCache.set(schemaKey, validate);
        }

        return this._validateCache.get(schemaKey);
    }

    // Apply default values from schema to data
    static applyDefaults(data) {
        const normalizedSchema = this.normalizeSchema(this._schema);

        // Normalize date values first (convert date-time to date format when needed)
        const dateNormalized = this.normalizeDateValues(data, normalizedSchema);

        // Remove undefined and empty string values (pass schema to know which fields are required)
        const cleanedData = this.removeUndefinedValues(
            dateNormalized,
            normalizedSchema
        );

        // Use Ajv to apply defaults
        const validate = ajv.compile(normalizedSchema);
        validate(cleanedData); // This applies defaults via useDefaults: true

        return cleanedData;
    }

    // Convert a Date instance or string to the requested JSON Schema format.
    // Returns the original value on parse failure or unsupported format.
    // Used by normalizeDateValues for both top-level fields and array items.
    //
    // Uses LOCAL TZ accessors deliberately. BaseEntity runs in the user's
    // browser, where "today" means the user's calendar day, not UTC's. This
    // matches the form picker (JsonSchemaForm.jsx uses local accessors too)
    // and how every native <input type="date"> behaves. For the rare Deno
    // serverless function path, containers default to UTC so local == UTC.
    static _convertDate(value, format) {
        if (value instanceof Date) {
            if (isNaN(value.getTime())) return value;
            if (format === "date") {
                const y = value.getFullYear();
                const m = String(value.getMonth() + 1).padStart(2, "0");
                const d = String(value.getDate()).padStart(2, "0");
                return `${y}-${m}-${d}`;
            }
            if (format === "date-time") return value.toISOString();
            return value;
        }
        if (typeof value !== "string" || !value) return value;
        if (format === "date") {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (dateRegex.test(value)) return value;
            try {
                const d = new Date(value);
                if (!isNaN(d.getTime())) {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    return `${y}-${m}-${day}`;
                }
            } catch {
                // fall through and return original
            }
            return value;
        }
        if (format === "date-time") {
            try {
                const d = new Date(value);
                if (!isNaN(d.getTime())) return d.toISOString();
            } catch {
                // fall through and return original
            }
            return value;
        }
        return value;
    }

    // Normalize date values to match schema format requirements
    // Converts date-time to date format when needed, handles various date formats
    static normalizeDateValues(obj, schema = null) {
        if (obj === null || typeof obj !== "object") {
            return obj;
        }

        // Date instances are objects (Date.keys() = []) — pass through here.
        // Format conversion happens in the parent loop where the format is known.
        if (obj instanceof Date) {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.normalizeDateValues(item, schema));
        }

        const normalized = {};
        const isJsonSchema = schema?.properties !== undefined;
        const schemaFields = isJsonSchema ? schema?.properties : schema || {};

        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            const fieldSchema = schemaFields[key];

            // Date / Date-time field — handles strings AND Date instances
            if (
                fieldSchema?.format === "date" ||
                fieldSchema?.format === "date-time"
            ) {
                if (
                    value instanceof Date ||
                    (typeof value === "string" && value)
                ) {
                    normalized[key] = this._convertDate(
                        value,
                        fieldSchema.format
                    );
                    return;
                }
            }

            // Array whose items have a date format, or whose items are objects
            // containing date fields — propagate the items schema into recursion.
            if (Array.isArray(value) && fieldSchema?.items) {
                const itemSchema = fieldSchema.items;
                if (
                    itemSchema.format === "date" ||
                    itemSchema.format === "date-time"
                ) {
                    normalized[key] = value.map((item) =>
                        this._convertDate(item, itemSchema.format)
                    );
                    return;
                }
                normalized[key] = value.map((item) =>
                    typeof item === "object" && item !== null
                        ? this.normalizeDateValues(item, itemSchema)
                        : item
                );
                return;
            }

            // Nested object — recurse with the field's sub-schema directly.
            // Don't gate on fieldSchema.type === "object"; sloppy schemas that
            // omit the explicit type still need their date fields normalized.
            if (typeof value === "object" && value !== null) {
                normalized[key] = this.normalizeDateValues(
                    value,
                    fieldSchema || null
                );
                return;
            }

            // Scalar value, no date handling needed
            normalized[key] = value;
        });

        return normalized;
    }

    // Extract top-level null values from an object so they can bypass validation
    // and be re-merged into the final payload. Used by create/bulkCreate/update
    // when the caller passes { allowNull: true } to explicitly clear fields.
    // Returns { nulls, withoutNulls } — only top-level keys are considered.
    static _extractNulls(obj) {
        const nulls = {};
        const withoutNulls = {};
        if (!obj || typeof obj !== "object") {
            return { nulls, withoutNulls: obj };
        }
        Object.keys(obj).forEach((key) => {
            if (obj[key] === null) {
                nulls[key] = null;
            } else {
                withoutNulls[key] = obj[key];
            }
        });
        return { nulls, withoutNulls };
    }

    // Remove undefined and empty string values from data
    // JSON Schema doesn't validate undefined - optional fields should simply be omitted
    // Empty strings are also treated as "not provided" for optional fields
    static removeUndefinedValues(obj, schema = null) {
        if (obj === null || typeof obj !== "object") {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.removeUndefinedValues(item, null));
        }

        const cleaned = {};
        const isJsonSchema = schema?.properties !== undefined;
        const schemaFields = isJsonSchema ? schema?.properties : schema || {};
        const requiredFields = isJsonSchema ? schema?.required || [] : [];

        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            const isRequired = isJsonSchema
                ? requiredFields.includes(key)
                : schemaFields[key]?.required;

            // Keep the value if:
            // 1. It's not undefined AND
            // 2. It's either required, or it's not an empty string (empty strings = not provided for optional fields)
            if (
                value !== undefined &&
                (isRequired || (value !== "" && value !== null))
            ) {
                // For nested objects, try to get the nested schema if available
                const nestedSchema =
                    schemaFields[key]?.type === "object"
                        ? schemaFields[key]
                        : null;
                cleaned[key] = this.removeUndefinedValues(value, nestedSchema);
            }
            // For optional fields, empty strings are treated as "not provided" and omitted
        });
        return cleaned;
    }

    // Validate data against schema
    static validate(data) {
        const normalizedSchema = this.normalizeSchema(this._schema);

        // Normalize date values first (convert date-time to date format when needed)
        const dateNormalized = this.normalizeDateValues(data, normalizedSchema);

        // Remove undefined and empty string values before validation
        // JSON Schema doesn't validate undefined - optional fields should simply be omitted
        // Empty strings are treated as "not provided" for optional fields
        const cleanedData = this.removeUndefinedValues(
            dateNormalized,
            normalizedSchema
        );
        const validate = this.getValidator();
        const valid = validate(cleanedData);

        if (!valid) {
            // Format Ajv errors into readable messages
            const errors = validate.errors.map((error) => {
                const path =
                    error.instancePath ||
                    error.params?.missingProperty ||
                    "root";
                const field =
                    path.replace(/^\//, "") ||
                    error.params?.missingProperty ||
                    "data";

                switch (error.keyword) {
                    case "required":
                        return `${error.params.missingProperty} is required`;
                    case "type":
                        return `${field} must be of type ${
                            error.params.type
                        }, got ${
                            Array.isArray(error.data)
                                ? "array"
                                : typeof error.data
                        }`;
                    case "minLength":
                        return `${field} must be at least ${error.params.limit} characters long`;
                    case "maxLength":
                        return `${field} must be at most ${error.params.limit} characters long`;
                    case "minimum":
                        return `${field} must be at least ${error.params.limit}`;
                    case "maximum":
                        return `${field} must be at most ${error.params.limit}`;
                    case "pattern":
                        return `${field} does not match the required pattern: ${error.params.pattern}`;
                    case "format":
                        return `${field} must be a valid ${error.params.format}`;
                    case "enum":
                        return `${field} must be one of: ${error.params.allowedValues.join(
                            ", "
                        )}`;
                    default:
                        return `${field}: ${error.message}`;
                }
            });

            throw new Error(`Validation errors: ${errors.join(", ")}`);
        }

        return true;
    }

    // Get collection/table name
    static getCollectionName() {
        return this.collectionName || this.name.toLowerCase() + "s";
    }

    // List all records
    static async list(sortField = null, limit = null, offset = null) {
        return await dbAdapter.list(this.getCollectionName(), sortField, limit, offset);
    }

    // Filter records
    static async filter(conditions, sortField = null, limit = null, offset = null) {
        return await dbAdapter.filter(
            this.getCollectionName(),
            conditions,
            sortField,
            limit,
            offset
        );
    }

    // Count records, optionally filtered by conditions
    static async count(conditions = null) {
        return await dbAdapter.count(this.getCollectionName(), conditions);
    }

    // Aggregate records with server-side computation (sum, avg, count, min, max)
    static async aggregate(options = {}) {
        return await dbAdapter.aggregate(this.getCollectionName(), options);
    }

    // Find by ID
    static async findById(id) {
        return await dbAdapter.findById(this.getCollectionName(), id);
    }

    // Get by ID (alias for findById)
    static async get(id) {
        return await this.findById(id);
    }

    // Check if record creation is blocked by usage limits
    static _checkUsageLimits() {
        if (typeof window === "undefined") return;
        const status = (window.__CONFIG__ || window.config || {}).usageLimitStatus;
        if (status?.limited && status?.rows) {
            const msg = `Record limit reached (${status.rowCount}/${status.maxRows} records). Please upgrade your plan to add more records.`;
            alert(msg);
            throw new Error(msg);
        }
    }

    // Create new record
    // options.allowNull (default false): when true, top-level null values in `data`
    //   bypass validation and are sent through to the database, allowing fields to
    //   be explicitly stored as null. Without this flag, nulls are stripped (legacy
    //   behavior) so optional fields with `format`/`minLength`/etc don't fail Ajv.
    //   FOOTGUN: nulls are extracted BEFORE Ajv runs, so a field passed as null
    //   bypasses ALL constraints on it — `required`, `format`, `enum`, `pattern`,
    //   `minLength`, etc. Only use allowNull on fields that should genuinely be
    //   nullable in the database. The DB column itself is the only remaining guard.
    static async create(data, options = null) {
        this._checkUsageLimits();
        const allowNull = options?.allowNull === true;

        // Pull out top-level nulls so they don't trip Ajv (type: "string" rejects null).
        // They get re-merged into the payload right before the DB call.
        const { nulls, withoutNulls } = allowNull
            ? this._extractNulls(data)
            : { nulls: {}, withoutNulls: data };

        const dataWithDefaults = this.applyDefaults(withoutNulls);
        this.validate(dataWithDefaults);

        const finalData = allowNull
            ? { ...dataWithDefaults, ...nulls }
            : dataWithDefaults;

        return await dbAdapter.create(this.getCollectionName(), finalData);
    }

    // Create multiple records at once
    // options.allowNull (default false): see BaseEntity.create for behavior and
    //   FOOTGUN warning. Applied per-record so each entry in `dataArray` can
    //   independently include nulls.
    static async bulkCreate(dataArray, options = null) {
        this._checkUsageLimits();
        const allowNull = options?.allowNull === true;

        // Process each record: extract nulls (if allowed), apply defaults, validate.
        const processed = dataArray.map((data) => {
            const { nulls, withoutNulls } = allowNull
                ? this._extractNulls(data)
                : { nulls: {}, withoutNulls: data };
            const dataWithDefaults = this.applyDefaults(withoutNulls);
            return { dataWithDefaults, nulls };
        });

        processed.forEach(({ dataWithDefaults }, index) => {
            try {
                this.validate(dataWithDefaults);
            } catch (error) {
                throw new Error(
                    `Validation failed for record ${index}: ${error.message}`
                );
            }
        });

        const finalRecords = processed.map(({ dataWithDefaults, nulls }) =>
            allowNull ? { ...dataWithDefaults, ...nulls } : dataWithDefaults
        );

        return await dbAdapter.bulkCreate(
            this.getCollectionName(),
            finalRecords
        );
    }

    // Update record
    // options.allowNull (default false): when true, top-level null values in `updates`
    //   bypass validation and are sent to the database, allowing fields to be
    //   explicitly cleared. Without this flag, nulls are stripped (legacy behavior)
    //   so cleared form fields don't fail Ajv against type/format/enum constraints.
    //   Example: Order.update(id, { assignedTo: null }, { allowNull: true })
    //   FOOTGUN: nulls are extracted BEFORE Ajv runs, so a field passed as null
    //   bypasses ALL constraints on it — `format`, `enum`, `pattern`, `minLength`,
    //   etc. Only use allowNull for fields the database column itself permits as
    //   null. The DB column is the only remaining guard against bad data.
    static async update(id, updates, options = null) {
        if (!id) {
            throw new Error(
                `${this.name}.update() requires a record key/id as the first argument. Usage: ${this.name}.update(record.key, { field: value }). You passed: ${this.name}.update(${JSON.stringify(id)}, ...). Make sure the record has a 'key' property - check that records are loaded correctly and the key is being passed.`
            );
        }
        if (!updates || typeof updates !== "object") {
            throw new Error(
                `${this.name}.update() requires an updates object as the second argument. Usage: ${this.name}.update(record.key, { field: value }).`
            );
        }

        const allowNull = options?.allowNull === true;

        // Pull out top-level nulls so they bypass Ajv (type: "string" rejects null).
        // They're re-merged into the cleaned payload right before the DB call.
        const { nulls, withoutNulls } = allowNull
            ? this._extractNulls(updates)
            : { nulls: {}, withoutNulls: updates };

        // Validate only the fields being updated
        // Support both JSON Schema format (with properties) and legacy format
        const isJsonSchema = this._schema.properties !== undefined;
        const schemaFields = isJsonSchema
            ? this._schema.properties
            : this._schema;

        const fieldsToValidate = {};
        Object.keys(withoutNulls).forEach((key) => {
            if (schemaFields[key]) {
                // Normalize the property schema to ensure correct types
                fieldsToValidate[key] = this.normalizePropertySchema(
                    schemaFields[key]
                );
            }
        });

        // Create a partial schema with only the fields being updated
        const partialSchema = isJsonSchema
            ? {
                  type: "object",
                  properties: fieldsToValidate,
                  required: [], // No required fields for partial updates
              }
            : fieldsToValidate;

        // Validate using the partial schema
        const normalizedSchema = this.normalizeSchema(partialSchema);

        // Normalize date values first (convert date-time to date format when needed)
        const dateNormalized = this.normalizeDateValues(
            withoutNulls,
            normalizedSchema
        );

        // Remove undefined and empty string values before validation
        const cleanedUpdates = this.removeUndefinedValues(
            dateNormalized,
            normalizedSchema
        );

        const validate = ajv.compile(normalizedSchema);
        const valid = validate(cleanedUpdates);

        if (!valid) {
            // Format Ajv errors into readable messages
            const errors = validate.errors.map((error) => {
                const path =
                    error.instancePath ||
                    error.params?.missingProperty ||
                    "root";
                const field =
                    path.replace(/^\//, "") ||
                    error.params?.missingProperty ||
                    "data";

                switch (error.keyword) {
                    case "required":
                        return `${error.params.missingProperty} is required`;
                    case "type":
                        return `${field} must be of type ${
                            error.params.type
                        }, got ${
                            Array.isArray(error.data)
                                ? "array"
                                : typeof error.data
                        }`;
                    case "minLength":
                        return `${field} must be at least ${error.params.limit} characters long`;
                    case "maxLength":
                        return `${field} must be at most ${error.params.limit} characters long`;
                    case "minimum":
                        return `${field} must be at least ${error.params.limit}`;
                    case "maximum":
                        return `${field} must be at most ${error.params.limit}`;
                    case "pattern":
                        return `${field} does not match the required pattern: ${error.params.pattern}`;
                    case "format":
                        return `${field} must be a valid ${error.params.format}`;
                    case "enum":
                        return `${field} must be one of: ${error.params.allowedValues.join(
                            ", "
                        )}`;
                    default:
                        return `${field}: ${error.message}`;
                }
            });

            throw new Error(`Validation errors: ${errors.join(", ")}`);
        }

        const finalUpdates = allowNull
            ? { ...cleanedUpdates, ...nulls }
            : cleanedUpdates;

        return await dbAdapter.update(
            this.getCollectionName(),
            id,
            finalUpdates
        );
    }

    // Update multiple records at once
    // Accepts an array of { key|id, ...updates }. Each entry is validated as a
    // partial update against the schema (same path as .update()), then sent
    // to the backend bulkupdate operation in a single round-trip.
    //
    // options.allowNull (default false): see BaseEntity.update for behavior and
    //   FOOTGUN warning. Applied per-record.
    //
    // Backend caps the batch at 200 records; callers should chunk above that.
    static async bulkUpdate(updatesArray, options = null) {
        if (!Array.isArray(updatesArray)) {
            throw new Error(
                `${this.name}.bulkUpdate() requires an array of { key, ...updates } objects.`
            );
        }
        const allowNull = options?.allowNull === true;
        const isJsonSchema = this._schema.properties !== undefined;
        const schemaFields = isJsonSchema
            ? this._schema.properties
            : this._schema;

        const prepared = updatesArray.map((entry, index) => {
            if (!entry || typeof entry !== "object") {
                throw new Error(
                    `${this.name}.bulkUpdate() entry ${index} is not an object.`
                );
            }
            const { key, id, ...rest } = entry;
            const recordId = key ?? id;
            if (!recordId) {
                throw new Error(
                    `${this.name}.bulkUpdate() entry ${index} is missing 'key' (or 'id'). Each entry must identify the record to update.`
                );
            }

            const { nulls, withoutNulls } = allowNull
                ? this._extractNulls(rest)
                : { nulls: {}, withoutNulls: rest };

            const fieldsToValidate = {};
            Object.keys(withoutNulls).forEach((field) => {
                if (schemaFields[field]) {
                    fieldsToValidate[field] = this.normalizePropertySchema(
                        schemaFields[field]
                    );
                }
            });

            const partialSchema = isJsonSchema
                ? {
                      type: "object",
                      properties: fieldsToValidate,
                      required: [],
                  }
                : fieldsToValidate;

            const normalizedSchema = this.normalizeSchema(partialSchema);
            const dateNormalized = this.normalizeDateValues(
                withoutNulls,
                normalizedSchema
            );
            const cleanedUpdates = this.removeUndefinedValues(
                dateNormalized,
                normalizedSchema
            );

            // H3 — cache compiled validators by field-subset signature so a
            // 500-row batch with N distinct field combinations compiles at
            // most N validators per batch, and repeats across batches hit
            // the cache instead of leaking refs into Ajv's internal store.
            const cacheKey =
                "partial:" +
                Object.keys(fieldsToValidate).sort().join(",");
            let validate = this._validateCache.get(cacheKey);
            if (!validate) {
                validate = ajv.compile(normalizedSchema);
                this._validateCache.set(cacheKey, validate);
            }
            if (!validate(cleanedUpdates)) {
                const errors = validate.errors.map((error) => {
                    const path =
                        error.instancePath ||
                        error.params?.missingProperty ||
                        "root";
                    const field =
                        path.replace(/^\//, "") ||
                        error.params?.missingProperty ||
                        "data";
                    return `${field}: ${error.message}`;
                });
                throw new Error(
                    `Validation failed for record ${index} (key=${recordId}): ${errors.join(", ")}`
                );
            }

            const finalUpdates = allowNull
                ? { ...cleanedUpdates, ...nulls }
                : cleanedUpdates;

            // M3 — spread updates first, pin `key` last so a stray `key`
            // field inside the update payload can't overwrite the id.
            return { ...finalUpdates, key: recordId };
        });

        return await dbAdapter.bulkUpdate(this.getCollectionName(), prepared);
    }

    // Delete record
    static async delete(id) {
        if (!id) {
            throw new Error(
                `${this.name}.delete() requires a record key/id as the argument. Usage: ${this.name}.delete(record.key). You passed: ${this.name}.delete(${JSON.stringify(id)}). Make sure the record has a 'key' property.`
            );
        }
        return await dbAdapter.delete(this.getCollectionName(), id);
    }

    // Get schema without built-in fields
    // Useful for JsonSchemaForm component — returns a deep clone so callers
    // (form renderers that decorate the schema with labels/defaults) cannot
    // accidentally mutate the entity's _schema by reference.
    static schema() {
        return JSON.parse(JSON.stringify(this._schema));
    }

    /**
     * Subscribe to realtime events for this entity's table.
     *
     *   const unsub = Messages.subscribe({ conversationId: "abc" }, (event) => {
     *       // event = { op, record, before?, seq, ... }
     *       if (event.op === "insert") setRows((r) => [...r, event.record]);
     *   });
     *
     * filter is optional (equality-only, evaluated server-side). Returns an
     * unsubscribe function. The first subscribe on the app opens a single
     * WebSocket; additional subscribes share it.
     *
     * Reconnects are automatic with exponential backoff and missed events
     * are replayed from the server-side event log — no app-level state
     * management required.
     *
     * The event's `record` and `before` are normalized to the same shape
     * that `.list()` / `.filter()` / `.get()` return — notably, `id` is
     * synthesized from `data.id || key` so `rows.map(r => <div key={r.id}>)`
     * works identically for list rows and realtime rows.
     */
    static subscribe(filter, onEvent) {
        const normalize = (row) =>
            row && typeof row === "object"
                ? { id: row.id ?? row.key, ...row }
                : row;
        return realtimeClient.subscribe(
            this.getCollectionName(),
            filter,
            (event) =>
                onEvent({
                    ...event,
                    record: normalize(event?.record),
                    before: normalize(event?.before),
                })
        );
    }
}

export { BaseEntity };
export default BaseEntity;
